'use client';

import { useCallback, useMemo } from 'react';
import {
  useAddFavoriteLocationsMutation,
  useDeleteFavoriteLocationsMutation,
  useGetFavoriteLocationsQuery,
  useGetLocationsQuery
} from 'services/locationsApi';

import { Box, useTheme } from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { getDistanceInMiles, useGeolocation } from 'utils';

import { ContentFilter, LocationInfoType, LocationInterface } from 'types';

import { Button, Loader, MultiStepHeader } from 'ui';
import { LocationBlock } from '../LocationBlock';
import { LocationInfoBanner } from '../LocationInfoBanner';

const LOCATION_RADIUS = 100;

interface NearMeLocationProps {
  onSelectHandler: (item: LocationInterface) => void;
  onBackHandler: () => void;
}

const NearMeLocation = ({
  onSelectHandler,
  onBackHandler
}: NearMeLocationProps) => {
  const {
    colors: {
      palette: {
        components: {
          locations: { locationPage: styles }
        }
      }
    }
  } = useTheme();
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { geoLocation, permissionStatus } = useGeolocation();

  const { data: locations = [], isLoading: isLocationsLoading } =
    useGetLocationsQuery(ContentFilter.ServicesOnly);
  const { data: favoriteLocations, isLoading: isFavLoading } =
    useGetFavoriteLocationsQuery('', {
      skip: !userInfo
    });
  const [addFavLocation] = useAddFavoriteLocationsMutation() || {};
  const [deleteFavLocation] = useDeleteFavoriteLocationsMutation() || {};

  const isLoading = isLocationsLoading || isFavLoading;

  const favoriteLocationIds = useMemo(
    () =>
      Array.isArray(favoriteLocations)
        ? favoriteLocations.map((loc) => loc.item_num)
        : [],
    [favoriteLocations]
  );

  const onFavoriteIconClick = useCallback(
    (isSelected: boolean, locationId: number) => {
      if (isSelected) {
        deleteFavLocation(locationId);
      } else {
        addFavLocation(locationId);
      }
    },
    [deleteFavLocation, addFavLocation]
  );

  const locationList =
    geoLocation !== null
      ? locations
          .filter((item) => item.name && item.city)
          .filter((loc) => {
            const distance = getDistanceInMiles(geoLocation, {
              longitude: loc.longitude ?? 0,
              latitude: loc.latitude ?? 0
            });

            const roundedDistance = Math.round(distance * 10) / 10;
            return roundedDistance <= LOCATION_RADIUS;
          })
          .sort((a, b) => {
            const distanceA = getDistanceInMiles(geoLocation, {
              longitude: a.longitude ?? 0,
              latitude: a.latitude ?? 0
            });
            const distanceB = getDistanceInMiles(geoLocation, {
              longitude: b.longitude ?? 0,
              latitude: b.latitude ?? 0
            });
            return distanceA - distanceB;
          })
      : [];

  const renderLocations = () =>
    locationList.length ? (
      <>
        <MultiStepHeader
          title='Locations Near My Current Location'
          subTitle='Please choose the preferred location for your appointment'
          headingColor={styles.headingColor}
          descriptionColor={styles.descriptionColor}
          mt={0}
          mb={{ base: '20px', md: '50px' }}
          descriptionPX={{}}
        />
        <LocationBlock
          isHidedHeader={false}
          locations={locationList}
          label={`LOCATIONS WITHIN ${LOCATION_RADIUS} MILES`}
          state=''
          onSelectLocation={onSelectHandler}
          onFavoriteIconClick={onFavoriteIconClick}
          favoriteLocationIds={favoriteLocationIds}
          isLogged={!!userInfo}
        />
        <Button
          colorScheme='black'
          onClick={onBackHandler}
          margin='50px auto 0'
          w='150px'
          padding={0}
        >
          Back
        </Button>
      </>
    ) : (
      <LocationInfoBanner
        goBack={onBackHandler}
        type={LocationInfoType.NO_NEAREST_LOCATION}
      />
    );

  return isLoading ? (
    <Loader absolute />
  ) : (
    <Box w='full' mt={{ base: '15px', md: 0 }}>
      {permissionStatus === 'denied' || permissionStatus === 'prompt' ? (
        <LocationInfoBanner
          goBack={onBackHandler}
          type={LocationInfoType.SERVICE_NOT_ACTIVATED}
        />
      ) : (
        renderLocations()
      )}
    </Box>
  );
};

export default NearMeLocation;
